import { usePermissions } from "@/hooks/use-permissions";
import { Navigate, useRouter, useRouterState } from "@tanstack/react-router";
import { ReactNode } from "react";
import { TabItem } from "@/components/custom/tabs/nav-tabs.tsx";
import Unauthorized from "@/routes/dashboard/unauthorized/-unauthorized.tsx";

const getPermissionByKey = (
  permissions: any,
  key: string,
): boolean | undefined => {
  return key.split(".").reduce((acc, k) => acc?.[k], permissions);
};

// A list is met when there is none, or when any one of its permissions is granted.
const isGranted = (permissions: any, required?: readonly string[]) =>
  !required || required.some((p) => getPermissionByKey(permissions, p));

interface PermissionFallbackProps {
  tabs?: TabItem[];
  children?: ReactNode;
}

export const PermissionFallback: React.FC<PermissionFallbackProps> = ({
  tabs, // used for redirect to first accessible tab
  children,
}) => {
  const { permissions } = usePermissions();
  const router = useRouter();
  // Subscribed rather than read once: a layout's PermissionFallback stays mounted while the user moves
  // between the pages under it (TanStack Router doesn't re-render a layout's component for a child
  // navigation), so it has to re-check each new set of matched routes itself.
  const matchedRouteIds = useRouterState({
    select: (state) => state.matches.map((match) => match.routeId),
  });

  // Every matched route's requiredPermissions must be met, not only the deepest one's: a section's
  // permission sits on its layout route (`/dashboard/data` declares features.sections.data) and has
  // to hold for every page under it, including pages that declare nothing of their own.
  const chain = matchedRouteIds.map((id) => router.looseRoutesById[id]);
  const currentRoute = chain.at(-1);
  const hasPermission = chain.every((route) =>
    isGranted(permissions, route?.requiredPermissions),
  );

  // Checked before the tab redirect, so a tabbed index never sends a user on past a route they fail.
  if (!hasPermission) {
    return <Unauthorized />;
  }

  // if the component has tabs, redirect to the first tab with permission
  if (tabs && Array.isArray(tabs)) {
    const firstAccessible = tabs.find(
      (tab) => tab.path && isGranted(permissions, tab.requiredPermissions),
    );

    if (firstAccessible?.path) {
      const targetPath = currentRoute?.fullPath?.endsWith("/")
        ? currentRoute.fullPath + firstAccessible.path
        : currentRoute?.fullPath + "/" + firstAccessible.path;

      return (
        <Navigate to={targetPath} search={firstAccessible?.search} replace />
      );
    }
  }

  return children;
};
